/**
 * Billing v2 Constants
 *
 * All constants use `as const` + type extraction pattern (NOT enums).
 * See: lib/auth/permissions.constants.ts for reference.
 */

// =============================================================================
// Billing Channels
// =============================================================================

export const BILLING_CHANNELS = {
  STRIPE: 'stripe',
  SHOPIFY: 'shopify',
} as const

export type BillingChannel = (typeof BILLING_CHANNELS)[keyof typeof BILLING_CHANNELS]

// =============================================================================
// Plan Statuses
// =============================================================================

export const PLAN_STATUSES = {
  DRAFT: 'draft',
  INTERNAL: 'internal',
  PUBLISHED: 'published',
  ARCHIVED: 'archived',
} as const

/** Statuses where the plan can be assigned to tenants and is exposed in pricing. */
export const ASSIGNABLE_PLAN_STATUSES = ['internal', 'published'] as const

export type PlanStatus = (typeof PLAN_STATUSES)[keyof typeof PLAN_STATUSES]

// =============================================================================
// Subscription Statuses
// =============================================================================

export const SUBSCRIPTION_STATUSES = {
  ACTIVE: 'active',
  PAST_DUE: 'past_due',
  CANCELED: 'canceled',
  TRIALING: 'trialing',
} as const

export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUSES)[keyof typeof SUBSCRIPTION_STATUSES]

/** Subscriptions that still hold their plan and block archiving it. */
export const LIVE_SUBSCRIPTION_STATUSES = [SUBSCRIPTION_STATUSES.ACTIVE, SUBSCRIPTION_STATUSES.TRIALING, SUBSCRIPTION_STATUSES.PAST_DUE]

// =============================================================================
// Payment Providers
// =============================================================================

export const PAYMENT_PROVIDERS = {
  STRIPE: 'stripe',
  SHOPIFY: 'shopify',
  /** Platform-managed billing — no external payment provider. Admin assigns plans, grants balance. */
  MANUAL: 'manual',
  /** Dev/test mock provider — local checkout simulation. NOT a business state for real tenants. */
  MOCK: 'mock',
} as const

export type PaymentProviderName = (typeof PAYMENT_PROVIDERS)[keyof typeof PAYMENT_PROVIDERS]

/**
 * How each provider's recurring Payment-to-Balance credit is triggered — the
 * single, exhaustive source of truth. There is no `default`, so adding a provider
 * to `PAYMENT_PROVIDERS` forces a decision here (the compiler flags the missing
 * case); a cron-billed provider can never be silently skipped.
 *
 * - `'webhook'` — credited by a per-charge webhook (Stripe `invoice.paid`); the
 *   billing-cycle cron must NOT also credit it.
 * - `'poll'`    — no per-renewal webhook; a dedicated poller credits per charge event
 *   (Shopify App Pricing — Partner-API Historical Events, keyed by event id).
 * - `'none'`    — collects no real recurring payment, so never earns the credit
 *   (manual comps, mock dev provider).
 */
export function recurringCreditTrigger(
  provider: PaymentProviderName,
): 'webhook' | 'poll' | 'none' {
  switch (provider) {
    case PAYMENT_PROVIDERS.STRIPE:
      return 'webhook'
    case PAYMENT_PROVIDERS.SHOPIFY:
      return 'poll'
    case PAYMENT_PROVIDERS.MANUAL:
    case PAYMENT_PROVIDERS.MOCK:
      return 'none'
  }
}

// =============================================================================
// Charge Types (billing_usage_charges)
// =============================================================================

export const CHARGE_TYPES = {
  TEXT: 'text',
  VOICE: 'voice',
  EMBEDDING: 'embedding',
  CRAWLER: 'crawler',
} as const

export type ChargeType = (typeof CHARGE_TYPES)[keyof typeof CHARGE_TYPES]

// =============================================================================
// Wallet Operation Types
// =============================================================================

export const OPERATION_TYPES = {
  USAGE_VOICE: 'usage_voice',
  USAGE_TEXT: 'usage_text',
  USAGE_EMBEDDING: 'usage_embedding',
  USAGE_CRAWLER: 'usage_crawler',
  TOPUP: 'topup',
  INITIAL_GRANT: 'initial_grant',
  PAYMENT_CREDIT: 'payment_credit',
  /** Historical: plan changes once refunded unused plan time as credits. No writer remains; not revenue. */
  PLAN_CHANGE_CREDIT: 'plan_change_credit',
  MANUAL_GRANT: 'manual_grant',
  MANUAL_DEDUCT: 'manual_deduct',
  REFUND: 'refund',
  /** A grant's remaining credits written off at its expiry; reference `wallet_grant` / grant id. */
  CREDIT_EXPIRY: 'credit_expiry',
} as const

export type OperationType = (typeof OPERATION_TYPES)[keyof typeof OPERATION_TYPES]

// =============================================================================
// Transaction Reference Types
// =============================================================================

export const REFERENCE_TYPES = {
  CALL: 'call',
  AI_REQUEST: 'ai_request',
  EMBEDDING_INDEX: 'embedding_index',
  EMBEDDING_QUERY: 'embedding_query',
  KB_URL_IMPORT: 'kb_url_import',
  STRIPE_PAYMENT: 'stripe_payment',
  SHOPIFY_PAYMENT: 'shopify_payment',
  MANUAL: 'manual',
  /** Starting credits; reference id `{tenantId}:{planId}`, so a plan grants them once per tenant. */
  INITIAL_GRANT: 'initial_grant',
  /** Shopify usage-meter top-up. Reference id = the `shopify_topup_attempts` row, so a replay credits once. */
  SHOPIFY_USAGE_TOPUP: 'shopify_usage_topup',
  WALLET_GRANT: 'wallet_grant',
} as const

export type ReferenceType = (typeof REFERENCE_TYPES)[keyof typeof REFERENCE_TYPES]

/** Grant source of the balance a wallet held before grants existed; it never expires. */
export const OPENING_BALANCE_GRANT_SOURCE = 'opening_balance'

/** Plan payment credits outlive their billing period by this much, covering renewal processing. */
export const PLAN_CREDITS_RENEWAL_BUFFER_MS = 24 * 60 * 60 * 1000

/** Welcome-redirect flag: the plan's monthly credits wait for Shopify to list the charge. */
export const SHOPIFY_CREDITS_PENDING_PARAM = 'shopify_credits_pending'

// =============================================================================
// Voice Trial Statuses
// =============================================================================

/** Trial duration when the plan leaves it unset (30 days). */
export const DEFAULT_VOICE_TRIAL_DURATION_HOURS = 720

export const VOICE_TRIAL_STATUSES = {
  ACTIVE: 'active',
  EXPIRED_TIME: 'expired_time',
  EXPIRED_BUDGET: 'expired_budget',
  UPGRADED: 'upgraded',
  CANCELLED: 'cancelled',
} as const

export type VoiceTrialStatus = (typeof VOICE_TRIAL_STATUSES)[keyof typeof VOICE_TRIAL_STATUSES]

// =============================================================================
// Monetary Constants
// =============================================================================

/** The wallet and plan prices are single-currency; provider amounts in any other currency are never converted. */
export const WALLET_CURRENCY = 'USD'

/** 1 USD = 1,000,000 microUSD */
export const MICRO_USD_PER_USD = 1_000_000

/** Customer credits: 1 credit = $0.01. Wallet storage stays in microUSD. */
export const MICRO_USD_PER_CREDIT = MICRO_USD_PER_USD / 100

/** Jina Reader paid token rate: $0.02 per 1M successful output tokens. */
export const JINA_READER_COST_PER_1M_TOKENS_MICROUSD = 20_000

/**
 * Conservative USD estimate for one SocialCrawl Starter-pack credit ($0.008).
 * The provider sells GBP packs, so set SOCIALCRAWL_COST_PER_CREDIT_MICROUSD to
 * the exact acquired USD cost for the active pack. Tenant-plan markup is then
 * applied through the normal crawler billing path.
 */
export const SOCIALCRAWL_DEFAULT_COST_PER_CREDIT_MICROUSD = 8_000

/** Crawl charges have no artificial minimum and retain exact microUSD precision. */
export const CRAWLER_MIN_CHARGE_MICROUSD = 0
export const CRAWLER_ROUNDING_DECIMALS = 6

/**
 * Hard floor for negative balance (in-flight voice calls): -$5.
 *
 * MUST stay in sync with the literal floor in the wallet_debit() SQL function
 * (supabase/migrations/db-review-functions.sql) — that SQL value is the
 * authoritative gate; this constant only backs WalletService.checkPreAuth().
 */
export const WALLET_BALANCE_FLOOR_MICROUSD = -5_000_000

/** Voice pre-auth safety margin: estimate cost for N minutes (configurable) */
export const VOICE_PREAUTH_SAFETY_MINUTES = 5

/** Default pool trial reserve percent */
export const DEFAULT_POOL_TRIAL_RESERVE_PERCENT = 20

/**
 * Fallback low-balance alert threshold ($5), used when `billing_settings` has no
 * row at all. The platform default lives in
 * `billing_settings.default_low_balance_threshold_microusd`; a tenant may lower
 * or raise it via `tenant_billing_settings.low_balance_threshold_microusd`.
 * `0` disables the low-balance alert (the depleted alert still fires).
 */
export const DEFAULT_LOW_BALANCE_THRESHOLD_MICROUSD = 5_000_000

/** 100,000 credits: catches a fat-fingered extra zero in a platform or tenant threshold. */
export const LOW_BALANCE_THRESHOLD_MAX_MICROUSD = 1_000_000_000

/** Default voice cost per minute in microUSD ($0.08/min) */
export const DEFAULT_VOICE_COST_PER_MIN_MICROUSD = 80_000

/** Orphan charge threshold: charges older than this with deducted=false are orphans */
export const ORPHAN_CHARGE_THRESHOLD_MS = 30 * 60 * 1000 // 30 minutes

/** Max orphan charges to process per reconciliation run */
export const RECONCILIATION_BATCH_LIMIT = 100

/** Duplicate detection lookback window */
export const DUPLICATE_DETECTION_LOOKBACK_MS = 24 * 60 * 60 * 1000 // 24 hours

// =============================================================================
// Billing Intervals
// =============================================================================

export const BILLING_INTERVALS = {
  MONTH: 'month',
  QUARTER: 'quarter',
  YEAR: 'year',
} as const

export type BillingIntervalType = (typeof BILLING_INTERVALS)[keyof typeof BILLING_INTERVALS]

// =============================================================================
// Shopify Usage Top-ups
// =============================================================================

/**
 * Reserved handle prefix for the Partner-Dashboard meters backing top-ups
 * (`topup-10` … `topup-500`). Separates them from the recurring plan line and from
 * a future genuine usage meter. Classifying by `usage != null` instead is unsafe:
 * `usage` is documented as populated for TIERED prices, so a flat-rate meter can
 * return `usage: null` and be mistaken for the plan.
 */
export const SHOPIFY_TOPUP_METER_PREFIX = 'topup-'

export function isShopifyTopupMeterHandle(handle: string | null | undefined): boolean {
  return typeof handle === 'string' && handle.startsWith(SHOPIFY_TOPUP_METER_PREFIX)
}

/**
 * `PENDING` covers "not sent" and "sent, outcome unknown" alike — after a timeout the
 * App Events API cannot tell them apart, and both are replayable. `FAILED` is only a
 * definitive rejection, which never reached Shopify's billing.
 */
export const SHOPIFY_TOPUP_ATTEMPT_STATUSES = {
  PENDING: 'pending',
  REPORTED: 'reported',
  CREDITED: 'credited',
  FAILED: 'failed',
} as const

export type ShopifyTopupAttemptStatus =
  (typeof SHOPIFY_TOPUP_ATTEMPT_STATUSES)[keyof typeof SHOPIFY_TOPUP_ATTEMPT_STATUSES]

/**
 * Attempt states that count toward a tenant's unbilled exposure for the current cycle.
 * The same set is reconciliation's upper bound — the two coincide by definition, since
 * an attempt that Shopify may still bill is exactly an attempt that may still cost the
 * merchant money. `FAILED` is excluded from both: it never reached Shopify's billing.
 */
export const SHOPIFY_TOPUP_EXPOSURE_STATUSES = [
  SHOPIFY_TOPUP_ATTEMPT_STATUSES.PENDING,
  SHOPIFY_TOPUP_ATTEMPT_STATUSES.REPORTED,
  SHOPIFY_TOPUP_ATTEMPT_STATUSES.CREDITED,
] as const

/** Attempt states we know Shopify accepted — reconciliation's lower bound, once settled. */
export const SHOPIFY_TOPUP_SENT_STATUSES = [
  SHOPIFY_TOPUP_ATTEMPT_STATUSES.REPORTED,
  SHOPIFY_TOPUP_ATTEMPT_STATUSES.CREDITED,
] as const

/** Attempt states the recovery sweep can still settle. */
export const SHOPIFY_TOPUP_UNSETTLED_STATUSES = [
  SHOPIFY_TOPUP_ATTEMPT_STATUSES.PENDING,
  SHOPIFY_TOPUP_ATTEMPT_STATUSES.REPORTED,
] as const

/**
 * Fallback ceiling per billing cycle ($500), used when `billing_settings` has no row.
 * App Pricing has no usage cap, so this limit is enforced app-side or not at all.
 */
export const DEFAULT_SHOPIFY_TOPUP_CYCLE_CAP_CENTS = 50_000

/** Months top-up credits last when Billing Settings has no row yet. */
export const DEFAULT_TOPUP_CREDIT_LIFETIME_MONTHS = 12

/** Sweep backoff — long enough that a purchase still in flight is never replayed under it. */
export const SHOPIFY_TOPUP_RETRY_DELAY_MS = 10 * 60_000

/** Attempts before a row is escalated, the purchase counting as the first. */
export const SHOPIFY_TOPUP_MAX_ATTEMPTS = 5

/**
 * Wall-clock the recovery sweep may spend inside one cron invocation. A replay against a
 * slow App Events API costs tens of seconds, and the same invocation still owes every
 * remaining store its renewal credit.
 */
export const SHOPIFY_TOPUP_SWEEP_BUDGET_MS = 20_000

/**
 * Shopify validates an accepted event asynchronously, so a fresh attempt is not yet in
 * `usage.quantity`. Reconciliation waits this long rather than reading the lag as a shortfall.
 */
export const SHOPIFY_TOPUP_SETTLING_GRACE_MS = 30 * 60_000

// =============================================================================
// Notification Types
// =============================================================================

export const BILLING_NOTIFICATION_TYPES = {
  LOW_BALANCE: 'low_balance',
  BALANCE_DEPLETED: 'balance_depleted',
  SPENDING_CAP_REACHED: 'spending_cap_reached',
  VOICE_TRIAL_EXPIRING: 'voice_trial_expiring',
  VOICE_TRIAL_EXPIRED: 'voice_trial_expired',
  PAYMENT_FAILED: 'payment_failed',
  SUBSCRIPTION_CANCELED: 'subscription_canceled',
  SUBSCRIPTION_EXTERNAL_CANCEL_FAILED: 'subscription_external_cancel_failed',
  RECONCILIATION_ORPHAN: 'reconciliation_orphan',
  RECONCILIATION_DUPLICATE: 'reconciliation_duplicate',
  DEDUCTION_FAILED: 'deduction_failed',
  /** A pool ElevenLabs account reached its real agent capacity (max_agents). */
  POOL_AGENTS_LIMIT_REACHED: 'pool_agents_limit_reached',
  WEBHOOK_ERROR: 'webhook_error',
  CRON_ERROR: 'cron_error',
  /** Top-up credited but unmatched by Shopify's usage counter, or unsettleable. Blocks top-ups until an operator clears it. */
  SHOPIFY_TOPUP_UNCONFIRMED: 'shopify_topup_unconfirmed',
} as const

export type BillingNotificationType =
  (typeof BILLING_NOTIFICATION_TYPES)[keyof typeof BILLING_NOTIFICATION_TYPES]

// =============================================================================
// Resource Types (for entitlement checks)
// =============================================================================

export const RESOURCE_TYPES = {
  VOICE_AGENT: 'voice_agents',
  CAMPAIGN: 'campaigns',
  PBX_NUMBER: 'pbx_numbers',
  PBX_FLOW: 'pbx_flows',
  KNOWLEDGE_BASE: 'knowledge_bases',
  KNOWLEDGE_BASE_SIZE_MB: 'knowledge_bases_size_mb',
  USER: 'users',
  CRM_INTEGRATION: 'crm_integrations',
  SHOPIFY_STORE: 'shopify_stores',
  MARKETING_CHANNEL: 'marketing_channels',
} as const

export type ResourceType = (typeof RESOURCE_TYPES)[keyof typeof RESOURCE_TYPES]

/** Resources checked against `tenant_usage_counters`; Shopify stores are counted live by `checkShopifyStoreLimit`. */
export type CountedResourceType = Exclude<ResourceType, typeof RESOURCE_TYPES.SHOPIFY_STORE>

// =============================================================================
// ElevenLabs Account Statuses (voice pool)
// =============================================================================

export const ELEVENLABS_ACCOUNT_STATUS = {
  ACTIVE: 'active',
  DISABLED: 'disabled',
  FULL: 'full',
} as const

export type ElevenLabsAccountStatus =
  (typeof ELEVENLABS_ACCOUNT_STATUS)[keyof typeof ELEVENLABS_ACCOUNT_STATUS]

// =============================================================================
// Permissions
// =============================================================================

export const BILLING_PERMISSIONS = {
  VIEW: 'billing.view',
  MANAGE: 'billing.manage',
  VIEW_ALL: 'billing.view_all',
  // Read-only access to the cross-tenant Tenants tab (no Plan change / Actions).
  VIEW_TENANTS: 'billing.view_tenants',
  MANAGE_PLANS: 'billing.manage_plans',
  MANAGE_TENANTS: 'billing.manage_tenants',
  GRANT_CREDITS: 'billing.grant_credits',
  MANAGE_ELEVENLABS_POOL: 'billing.manage_elevenlabs_pool',
} as const

// =============================================================================
// Webhook Event Types (Stripe events we handle)
// =============================================================================

export const WEBHOOK_EVENT_TYPES = {
  // Stripe
  CHECKOUT_SESSION_COMPLETED: 'checkout.session.completed',
  INVOICE_PAID: 'invoice.paid',
  INVOICE_PAYMENT_FAILED: 'invoice.payment_failed',
  SUBSCRIPTION_UPDATED: 'customer.subscription.updated',
  SUBSCRIPTION_DELETED: 'customer.subscription.deleted',
  /**
   * Stripe pending-update apply: the immediate invoice for a `pending_if_incomplete`
   * upgrade has been paid; items / billing_cycle changes are now live on the
   * subscription. `previous_attributes` carries the OLD period dates, which close
   * the old period's usage.
   */
  SUBSCRIPTION_PENDING_UPDATE_APPLIED: 'customer.subscription.pending_update_applied',
  /**
   * Stripe pending-update expiry: payment failed or the pending invoice expired
   * (~23h). Stripe voids the invoice and discards the staged update — items and
   * billing_cycle revert to their pre-pending values. We clear the
   * `pending_plan_change_initiated_at` UI hint and surface a warning notification.
   */
  SUBSCRIPTION_PENDING_UPDATE_EXPIRED: 'customer.subscription.pending_update_expired',
} as const

export type WebhookEventType = (typeof WEBHOOK_EVENT_TYPES)[keyof typeof WEBHOOK_EVENT_TYPES]

// =============================================================================
// Mock Session Types & Statuses
// =============================================================================

export const MOCK_SESSION_TYPES = {
  SUBSCRIPTION: 'subscription',
  PAYMENT: 'payment',
} as const

export type MockSessionType = (typeof MOCK_SESSION_TYPES)[keyof typeof MOCK_SESSION_TYPES]

export const MOCK_SESSION_STATUSES = {
  PENDING: 'pending',
  COMPLETED: 'completed',
  FAILED: 'failed',
} as const

export type MockSessionStatus = (typeof MOCK_SESSION_STATUSES)[keyof typeof MOCK_SESSION_STATUSES]

// =============================================================================
// Plan Change
// =============================================================================

/**
 * Cooldown window between consecutive upgrade attempts. The original
 * per-period throttle (`last_plan_change_at >= current_period_start`) does not
 * survive an upgrade because `billing_cycle_anchor: 'now'` resets
 * `current_period_start` on every successful upgrade — the gate would
 * permanently fire. 10 minutes is enough to absorb double-clicks and webhook
 * settling without blocking a legitimate retry after a transient failure. See
 * `docs/PLAN_CHANGE.md` → "Local steps" for the full gate sequence.
 */
export const PLAN_CHANGE_UPGRADE_COOLDOWN_MS = 10 * 60 * 1000 // 10 minutes

export const DEFAULT_KNOWLEDGE_BASES_LIMIT = 500
